import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useServicesContext } from '../../contexts/ServicesContext';
import type { LeadStatus, Lead, LeadListResponse, PipelineStats, CreateLeadPayload, ConvertLeadPayload, UpdateLeadPayload, AddLeadActivityPayload, LeadActivityType } from '../../api/queries';

/**
 * Custom Hooks for Leads
 *
 * Declarative data fetching and mutations using ServicesContext.
 * Follows Hooks pattern for separation of concerns.
 */

// ============================================================================
// QUERY KEYS
// ============================================================================

export const LEADS_QUERY_KEY = ['leads'];
export const PIPELINE_STATS_QUERY_KEY = ['leads', 'stats'];

export function useLeads(filters?: { status?: LeadStatus; search?: string }) {
  const { getLeads } = useServicesContext();

  return useQuery({
    queryKey: [...LEADS_QUERY_KEY, filters],
    queryFn: () => getLeads(filters),
    select: (data: LeadListResponse) => data.leads,
  });
}

export function useGetLeadDetail(leadId: number) {
  const { getLeadDetail } = useServicesContext();

  return useQuery({
    queryKey: [...LEADS_QUERY_KEY, leadId],
    queryFn: () => getLeadDetail(leadId),
    enabled: !!leadId,
  });
}

export function usePipelineStats() {
  const { getPipelineStats } = useServicesContext();

  return useQuery({
    queryKey: PIPELINE_STATS_QUERY_KEY,
    queryFn: getPipelineStats,
  });
}

export function useCreateLead() {
  const { createLead } = useServicesContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LEADS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PIPELINE_STATS_QUERY_KEY });
    },
  });
}

export function useUpdateLead() {
  const { updateLead } = useServicesContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: number; payload: UpdateLeadPayload }) =>
      updateLead(leadId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LEADS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PIPELINE_STATS_QUERY_KEY });
    },
  });
}

export function useConvertLead() {
  const { convertLeadToCustomer } = useServicesContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: number; payload?: ConvertLeadPayload }) =>
      convertLeadToCustomer(leadId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LEADS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PIPELINE_STATS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['GET_PARTNER_CUSTOMERS'] });
    },
  });
}

export function useAddLeadActivity() {
  const { addLeadActivity } = useServicesContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: number; payload: AddLeadActivityPayload }) =>
      addLeadActivity(leadId, payload),
    onSuccess: (_data, variables) => {
      // Invalidate specific lead detail to refresh activities list
      queryClient.invalidateQueries({ queryKey: [...LEADS_QUERY_KEY, variables.leadId] });
      // Invalidate list and stats to reflect updated lastActivityDate
      queryClient.invalidateQueries({ queryKey: LEADS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PIPELINE_STATS_QUERY_KEY });
    },
  });
}

export function useDeleteLead() {
  const { deleteLead } = useServicesContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: number) => deleteLead(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LEADS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PIPELINE_STATS_QUERY_KEY });
    },
  });
}

// ============================================================================
// DERIVED STATE HOOKS (Compose & Transform pattern)
// ============================================================================

export function useLeadsByStatus(status: LeadStatus) {
  const { data: leads, ...rest } = useLeads({ status });
  return { leads: leads ?? [], ...rest };
}

export function useUpcomingFollowUps() {
  const { data: stats } = usePipelineStats();
  return stats?.upcomingFollowUps ?? [];
}

export function useLeadValue(lead: Lead | null) {
  if (!lead) return { monthly: 0, yearly: 0, cashback: 0 };

  return {
    monthly: lead.estimatedMonthlyValueNOK,
    yearly: lead.estimatedYearlyValueNOK,
    cashback: lead.estimatedMonthlyCashbackNOK,
  };
}

// Re-export types for convenience
export type { Lead, LeadStatus, PipelineStats, CreateLeadPayload, ConvertLeadPayload, UpdateLeadPayload, LeadActivityType, AddLeadActivityPayload };
